/**
 * useActivityFeed Hook
 *
 * React Query hook for fetching dispatcher activity feed events.
 * Used by ActivityFeed component.
 *
 * Features:
 * - 2-minute fallback polling (socket events provide real-time updates)
 * - Pauses polling when tab is hidden, refetches on focus
 * - Event type filtering
 * - Transform backend events to frontend format
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  fetchActivityFeed,
  ActivityFeedResponse,
  ActivityEventResponse,
  ActivityFeedParams,
} from '../../../../../api/dispatch';
import { ActivityEvent, ActivityEventType } from '../../components/guardLocation/ActivityFeed';

/** Query key for activity feed */
export const ACTIVITY_FEED_QUERY_KEY = ['dispatch', 'activityFeed'];

/** Polling interval (2 minutes — socket events handle real-time, polling is fallback) */
const REFETCH_INTERVAL = 2 * 60 * 1000;

/** Stale time (60 seconds) */
const STALE_TIME = 60 * 1000;

export interface UseActivityFeedOptions {
  /** Maximum number of events to fetch (default: 50) */
  limit?: number;
  /** Filter by event types (comma-separated) */
  types?: string;
  /** Override polling interval (ms) */
  refetchInterval?: number;
  /** Disable auto-polling */
  enablePolling?: boolean;
}

export interface UseActivityFeedResult {
  /** Activity events transformed for frontend */
  events: ActivityEvent[];
  /** Raw API response */
  rawData: ActivityFeedResponse | null;
  /** Whether more events are available */
  hasMore: boolean;
  /** Whether initial load is in progress */
  isLoading: boolean;
  /** Whether any fetch is in progress */
  isFetching: boolean;
  /** Whether an error occurred */
  isError: boolean;
  /** Error object if present */
  error: Error | null;
  /** Last successful update timestamp */
  dataUpdatedAt: number | undefined;
  /** Manually trigger a refresh */
  refetch: () => void;
}

/**
 * Transform backend event to frontend ActivityEvent format
 */
function transformEvent(event: ActivityEventResponse): ActivityEvent {
  // Map backend types to frontend types (they're mostly the same)
  const typeMap: Record<string, ActivityEventType> = {
    patrol_started: 'patrol_started',
    patrol_completed: 'patrol_completed',
    checkpoint_completed: 'checkpoint_completed',
    guard_arrived: 'guard_arrived',
    guard_departed: 'guard_departed',
  };

  return {
    eventId: event.eventId,
    type: typeMap[event.type] || ('patrol_started' as ActivityEventType),
    guardId: event.guardId,
    guardName: event.guardName,
    timestamp: event.timestamp,
    details: {
      patrolId: event.details.patrolId,
      siteName: event.details.siteName,
      checkpointName: event.details.checkpointName,
      progress: event.details.progress,
    },
  };
}

/**
 * Hook to fetch dispatcher activity feed
 *
 * @param options - Configuration options
 * @returns Activity events with loading/error states
 */
export function useActivityFeed(
  options: UseActivityFeedOptions = {}
): UseActivityFeedResult {
  const {
    limit = 50,
    types,
    refetchInterval = REFETCH_INTERVAL,
    enablePolling = true,
  } = options;

  const params: ActivityFeedParams = {
    limit,
    types,
  };

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    dataUpdatedAt,
    refetch,
  } = useQuery({
    queryKey: [...ACTIVITY_FEED_QUERY_KEY, params],
    queryFn: () => fetchActivityFeed(params),
    refetchInterval: enablePolling ? refetchInterval : false,
    staleTime: STALE_TIME,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  // Transform events to frontend format
  const events = useMemo(() => {
    if (!data?.events) return [];
    return data.events.map(transformEvent);
  }, [data?.events]);

  return {
    events,
    rawData: data ?? null,
    hasMore: data?.hasMore ?? false,
    isLoading,
    isFetching,
    isError,
    error: error instanceof Error ? error : null,
    dataUpdatedAt,
    refetch,
  };
}

export default useActivityFeed;
